import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useErrorBoundary } from 'react-error-boundary';
import Mustache from 'mustache';
import { AppContext } from '../util/AppContext';
import { getConfigValue, getConfigCollection } from '../util/getConfigValue';
import getName from '../util/getName';
import { ProductsModal, ProductsModalButton } from '../components/ProductsModal';
import MessageDetailsModal from '../components/MessageDetailsModal';
import PreviewModal from '../components/PreviewModal';
import ToastAlert from '../components/ToastAlert';
import CategoryDropdown from '../components/CategoryDropdown';
import PrintOptionDropdown from '../components/printOptions/printOptionDropdown';
import ContainerMessageTable from '../components/container-messaging/containerMessageTable';
import useScrollPosition from '../hooks/useScrollPosition';
import { getContainerMessage } from '../API/containerMessage';
import MessageTypes from '../util/messageTypes';
import { documentGenerateContainer, documentGenerateContainerPlanless, documentGenerationType } from '../API/documentGenerate';
import executePrintOptionRestrictions from '../util/printOptionRestrictions';
import { useRoutingValidation } from '../util/useRoutingValidation';
import { FavoriteDesignation, updateHcpAppState } from "../components/FavoriteDesignation";
import { useBreadcrumbs } from '../util/BreadcrumbContext';
import CreateSalesAidStepProgressBar from '../components/CreateSalesAidStepProgressBar';
import { SalesAidStepIds } from '../util/salesAidNavigation';
import { getPrescriberTypeLabel } from '../util/getHcpLabel';


function ContainerMessagePage() {
	const [appState, setAppState] = useContext(AppContext);
	const { showBoundary } = useErrorBoundary();
	const scrollPosition = useScrollPosition();
	const navigate = useNavigate();
	const configMenuRef = useRef(null);
	const configMenuContentRef = useRef(null);

	const [isLoading, setIsLoading] = useState(false);
	const [isConfigMenuVisible, setIsConfigMenuVisible] = useState(false);

	const [minPlans, setMinPlans] = useState(null);
	const [maxPlans, setMaxPlans] = useState(null);
	const [prescriberId] = useState(appState.prescriber?.id);
	const [productImageSource, setProductImageSource] = useState(appState?.currentProduct?.imagePath);
	const [currentProduct, setCurrentProduct] = useState(appState?.currentProduct);
	const [categories, setCategories] = useState([]);
	const [pageCategory, setPageCategory] = useState(appState.category);
	const [messageCode, setMessageCode] = useState(appState.category?.messageCode);
	const prescriberType = appState?.prescriberType;
	const [hasPlanTable, setHasPlanTable] = useState(true);
	const [plans, setPlans] = useState(appState.plans);
	const [selectedPlansByMessage, setSelectedPlansByMessage] = useState([]);
	const [selectedMessages, setSelectedMessages] = useState([]);
	const [selectedColumnState, setSelectedColumnState] = useState([]);
	const [prescriberMessages, setPrescriberMessages] = useState([]);

	const [maxDisplayColumns, setMaxDisplayColumns] = useState(5);
	const [exceedMaxColumns, setExceedMaxColumns] = useState(false);
	const minPrintColumns = getConfigValue(appState.config.PlanList.MinPrintableColumns, appState)?.Value || 3;
	const maxPrintColumns = getConfigValue(appState.config.PlanList.MaxPrintableColumns, appState)?.Value || 5;
	
	const [pdfFileName, setPdfFileName] = useState('');
	const [pdfUrl, setPdfUrl] = useState('');

	const planListConfig = useMemo(() => getConfigCollection(appState.config?.PlanList, appState, 'PlanList'), [appState]);

	const hcpNameRequired = getConfigValue(appState.config?.PrintOptions?.HCPName?.Required, appState)?.Value === 'true';
	const ehrBackPageRequired = getConfigValue(appState.config?.PrintOptions?.EhrBackPage?.Required, appState)?.Value === 'true';
	const hcpCoverPageRequired = getConfigValue(appState.config?.PrintOptions?.HCP?.CoverPage?.Required, appState)?.Value === 'true';
	const savingsCardRequired = getConfigValue(appState.config?.PrintOptions?.SavingsCard?.Required, appState)?.Value === 'true';
	const locationNameRequired = getConfigValue(appState.config?.PrintOptions?.IncludeLocationName?.Required, appState)?.Value === 'true';
	const salesRepInfoRequired = getConfigValue(appState.config?.PrintOptions?.SalesRepInfo?.Required, appState)?.Value === 'true';
	const frmInfoRequired = getConfigValue(appState.config?.PrintOptions?.FRMInfo?.Required, appState)?.Value === 'true';

	const [configHCPisChecked, setConfigHCPisChecked] = useState(hcpNameRequired || getConfigValue(appState.config?.PrintOptions?.HCPName?.Default, appState)?.Value === 'true');
	const [configEHRisChecked, setConfigEHRisChecked] = useState(
		ehrBackPageRequired || getConfigValue(appState.config?.PrintOptions?.EhrBackPage?.Default, appState)?.Value === 'true'
	);
	const printOptionsEhrBackPageEnabled = getConfigValue(appState.config.PrintOptions?.EhrBackPage?.Enabled, appState)?.Value === 'true';
	const printOptionsCoverPageEnabled = getConfigValue(appState.config?.PrintOptions?.HCP?.CoverPage?.Enabled, appState)?.Value === 'true';
	const printOptionsHcpNameEnabled = getConfigValue(appState.config?.PrintOptions?.HCPName?.Enabled, appState)?.Value === 'true';
	const printOptionsLocationNameEnabled = getConfigValue(appState.config?.PrintOptions?.IncludeLocationName?.Enabled, appState)?.Value === 'true';
	const [printOptionsCoverPageIsChecked, setPrintOptionsCoverPageIsChecked] = useState(
		hcpCoverPageRequired || getConfigValue(appState.config?.PrintOptions?.HCP?.CoverPage?.Default, appState)?.Value === 'true'
	);
	const [configLocationNameIsChecked, setConfigLocationNameIsChecked] = useState(
		locationNameRequired || getConfigValue(appState.config.PrintOptions?.IncludeLocationName?.Default, appState)?.Value === 'true'
	);
	const [savingsCardIsChecked, setSavingsCardIsChecked] = useState(
		savingsCardRequired || getConfigValue(appState.config?.PrintOptions?.SavingsCard?.Default, appState)?.Value === 'true'
	);
	const savingsCardIsEnabled = getConfigValue(appState.config?.PrintOptions?.SavingsCard?.Enabled, appState)?.Value === 'true';

	const [showToast, setShowToast] = useState(false);
	const [toastTitle, setToastTitle] = useState('');
	const [toastMessage, setToastMessage] = useState('');
	const [toastAutohide, setToastAutohide] = useState(true);

	const [showProductsModal, setShowProductsModal] = useState(false);
	const [showMessageDetailsModal, setShowMessageDetailsModal] = useState(false);
	const [showPreviewModal, setShowPreviewModal] = useState(false);
	const [selectionMessage, setSelectionMessage] = useState('Loading...');
	const [selectableOptions, setSelectableOptions] = useState({});
	const [territoryId] = useState(appState.territory?.id);
	const [marketId] = useState(appState.market?.id);

	const printOptionsSelectSalesRepInfoEnabled = getConfigValue(appState.config.PrintOptions?.SalesRepInfo?.Enabled, appState)?.Value === 'true';
	const printOptionsSelectFRMInfoEnabled = getConfigValue(appState.config.PrintOptions?.FRMInfo?.Enabled, appState)?.Value === 'true';

	const isSingleOptionSelectionEnabled =
		getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.SelectSingleOption?.Enabled, appState)?.Value === 'true';
	const isSingleOptionFormularyDrillDownEnabled =
		getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.SelectSingleOption?.FormularyDrillDown?.Selectable, appState)?.Value === 'true';
	const isSingleOptionSavingsCardEnabled =
		getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.SelectSingleOption?.SavingsCard?.Selectable, appState)?.Value === 'true';
	const isSingleOptionRestrictionDetailsEnabled =
		getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.SelectSingleOption?.RestrictionDetails?.Selectable, appState)?.Value === 'true';
	const isSingleOptionSpecialtyPharmacyEnabled =
		getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.SelectSingleOption?.SpecialtyPharmacy?.Selectable, appState)?.Value === 'true';

	const [printOptionsSalesRepInfoIsChecked, setPrintOptionsSalesRepInfoIsChecked] = useState(
		salesRepInfoRequired || getConfigValue(appState.config?.PrintOptions?.SalesRepInfo?.Default, appState)?.Value === 'true'
	);

	const [printOptionsFRMInfoInfoIsChecked, setPrintOptionsFRMInfoInfoIsChecked] = useState(
		frmInfoRequired || getConfigValue(appState.config?.PrintOptions?.FRMInfo?.Default, appState)?.Value === 'true'
	);
	const { setBreadcrumbs } = useBreadcrumbs();

	useEffect(() => {
		setBreadcrumbs([
			{ label: getConfigValue(appState.config?.Focus?.Header, appState)?.Value, path: "/FocusPage" }
		]);
	}, [setBreadcrumbs]);

	useRoutingValidation();

	function updateSelectedPlans(messageCode, selectedPlans) {
		const item = { messageCode, selectedPlans };

		//if no plans are selected, remove from selectedPLansByMessage
		if (selectedPlans.length === 0) {
			setSelectedPlansByMessage((originalPlans) => originalPlans.filter((p) => p.messageCode !== item.messageCode));
			return;
		}
		//update the collection with the new item
		setSelectedPlansByMessage((originalPlans) => [...originalPlans.filter((p) => p.messageCode !== item.messageCode), item]);
	}

	function getAllSelectedPlans() {
		let selectedPlans = [];
		for (let message of selectedPlansByMessage) {
			selectedPlans = selectedPlans.concat(message.selectedPlans);
		}
		return selectedPlans;
	}
	const parentState = {
		isLoading: isLoading,
		isSingleSelection: false,
		savingsCardIsChecked: savingsCardIsChecked,
		selectionMessage: selectionMessage,
		exceedMaxColumns: exceedMaxColumns,
		maxDisplayColumns: maxDisplayColumns,
		isImprovedAccessStatic: false,
		selectableOptions: selectableOptions,
		isSingleOptionSelectionEnabled: isSingleOptionSelectionEnabled,
		isSingleOptionFormularyDrillDownEnabled: isSingleOptionFormularyDrillDownEnabled,
		isSingleOptionRestrictionDetailsEnabled: isSingleOptionRestrictionDetailsEnabled,
		isSingleOptionSpecialtyPharmacyEnabled: isSingleOptionSpecialtyPharmacyEnabled,
		isSingleOptionSavingsCardEnabled: isSingleOptionSavingsCardEnabled,
		selectedPlans: getAllSelectedPlans(),
		preSelectedPlans: []
	};

	function showTooManyOptionsSelectedMessage() {
		let availableOptions = [];

		if (isSingleOptionFormularyDrillDownEnabled) {
			availableOptions.push(getConfigValue(appState.config?.PlanList?.Options?.Formularies, appState)?.Value);
		}

		if (isSingleOptionRestrictionDetailsEnabled) {
			availableOptions.push(getConfigValue(appState.config?.PlanList?.Options?.Restrictions, appState)?.Value);
		}

		if (isSingleOptionSavingsCardEnabled) {
			availableOptions.push(getConfigValue(appState.config?.PlanList?.ConfigureOption?.SavingsCard, appState)?.Value);
		}

		if (isSingleOptionSpecialtyPharmacyEnabled) {
			availableOptions.push(getConfigValue(appState.config?.PlanList?.Options?.SpecialtyPharmacy, appState)?.Value);
		}

		const errorMessage = getConfigValue(
			appState.config?.PlanList?.Options?.IncludeInPrint?.SelectSingleOption?.TooManyItemsSelected?.Error,
			appState
		)?.Value;
		const selectableOptions = availableOptions.join(' or ').toLowerCase();

		triggerToastAlert(
			getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.SelectSingleOption?.TooManyItemsSelected?.Title, appState)?.Value,
			Mustache.render(errorMessage, { selectableOptions }),
			false
		);
	}

	function isPageValid() {
		if (!hasPlanTable) {
			return selectedMessages.length > 0;
		}
		// check at least one message has selections
		if (selectedPlansByMessage.length === 0) {
			return false;
		}
		//check min selected columns
		if (selectedColumnState.length < minPrintColumns) {
			return false;
		}

		for (let message of selectedPlansByMessage) {
			//check min/max selected plans for each message
			if (message.selectedPlans.length < minPlans || message.selectedPlans.length > maxPlans) {
				return false;
			}
			//check soft restrictions for each message plan
			for (let plan of message.selectedPlans) {
				if (
					plan.minPrintPlans &&
					parseInt(plan.minPrintPlans) > 0 &&
					message.selectedPlans.filter((e) => e.planClass !== plan.planClass).length < parseInt(plan.minPrintPlans)
				) {
					return false;
				}
			}
		}
		return true;
	}

	function handleMessageCheckboxChange(message, checked) {
		if (checked) {
			setSelectedMessages((prev) => [...prev, message]);
		} else {
			setSelectedMessages((prev) =>
				prev.filter((m) => m.prescriberMessage.id !== message.prescriberMessage.id)
			);
		}
	}

	function isMessageSelected(message) {
		return selectedMessages.some((m) => m.prescriberMessage.id === message.prescriberMessage.id);
	}
	const isValid = isPageValid();

	useEffect(() => {
		let message = getConfigValue(appState.config.PlanList.MinMaxPrintMessage, appState)?.Value;
		setSelectionMessage(Mustache.render(message, planListConfig));
	}, [planListConfig, appState]);

	useEffect(() => {
		if (categories === null || categories.length === 0) {
			getMessageCategories();
		}

		updateSelectMessageCategory(pageCategory);

		// set the initial controls
		setSelectableOptions({
			canSelectFormulary: true,
			canSelectRestrictions: true,
			canSelectSpecialtyPharmacy: true,
			canSelectSavingsCard: true
		});
	}, []);

	useEffect(() => {
		if (appState?.config?.PlanList?.MaxDisplayColumns) {
			setMaxDisplayColumns(parseInt(getConfigValue(appState.config.PlanList.MaxDisplayColumns, appState)?.Value));
		}
	}, [appState]);

	useEffect(() => {
		if (messageCode && prescriberId) {
			loadMessages();
		}
		// eslint-disable-next-line
	}, [messageCode, prescriberId, appState?.config]);

	function loadMessages() {
		getContainerMessage(messageCode, prescriberId, appState.currentProduct.id, appState.category.messageTypeId, false, prescriberType)
			.then((responseJson) => {
				setPrescriberMessages(responseJson);
				setSelectedMessages([]);
				const responsePlans = responseJson[0]?.dynamicTable;
				const minPlans = getConfigValue(appState.config.PlanList.MinPrintablePlans, appState)?.Value || null;
				const maxPrintPlans = getConfigValue(appState.config.PlanList.MaxPrintablePlans, appState)?.Value || 6;
				setPlans(responsePlans);
				setMinPlans(minPlans);
				setMaxPlans(maxPrintPlans);
				setHasPlanTable(responseJson[0]?.prescriberMessage?.hasPlanTable);
				if (responsePlans?.headers) {
					//pre select the columns to the max number of visible columns defined
					const visibleColumns = [];
					for (const header of responsePlans.headers) {
						if ((header.isRequired || header.autoSelect) && visibleColumns.length < maxPrintColumns) {
							visibleColumns.push(header.key);
						}
					}
					setSelectedColumnState(visibleColumns);
					setExceedMaxColumns(responsePlans?.headers.length > maxDisplayColumns);
				}
			})
			.catch((e) => showBoundary(e));
	}

	function getMessageCategories() {
		if (appState.products.length > 0) {
			const categories = appState.products.find((x) => {
				return x.productId === appState.currentProduct.clientProductId;
			});
			setCategories(categories.messages);
		}
	}

	async function loadDoc(generationType) {
		setIsLoading(true);

		const baseProps = {
			productId: currentProduct.clientProductId,
			marketid: Number(marketId),
			printPrescriberName: printOptionsHcpNameEnabled && configHCPisChecked,
			addEHRBackPage: configEHRisChecked,
			prescriberId: prescriberId,
			messageType: pageCategory.messageCode,
			territoryid: territoryId,
			addCoverPage: printOptionsCoverPageIsChecked,
			generateFor: generationType,
			messageTypeId: pageCategory.messageTypeId,
			columnKeys: hasPlanTable ? selectedColumnState : [],
			improvedAccessPlan: null,
			includeLocationName: printOptionsLocationNameEnabled && configLocationNameIsChecked,
			includeSavingsCard: savingsCardIsChecked,
			targetLevel: prescriberType,
			includeSalesRep: printOptionsSalesRepInfoIsChecked,
			includeFRM: printOptionsFRMInfoInfoIsChecked,
			timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			purchaseOrderNumber: appState.purchaseOrderNumber
		};

		const props = hasPlanTable
			? {
					...baseProps,
					selections: selectedPlansByMessage.map((message) => ({
						messageCode: message.messageCode,
						selectedPlans: message.selectedPlans.map((plan) => ({
							planId: plan.planId,
							includeRestrictions: plan.includeRestrictions,
							includeFormularies: plan.includeFormularies,
							includeSpecialtyPharmacy: plan.includeSpecialtyPharmacy,
							competitiveProductPrintOptions: plan.includeCompetitiveRestrictionNotes ?? [],
						})),
					})),
			  }
			: {
					...baseProps,
					selections: selectedMessages.map((message) => ({
						messageCode: message.prescriberMessage.messageCode,
					})),
			  };

		const generateFn = hasPlanTable ? documentGenerateContainer : documentGenerateContainerPlanless;
		const response = await generateFn(props)
			.then((responseJson) => {
				setIsLoading(false);

				// store the properties of the last document generated
				setAppState({
					...appState,
					lastGeneratedDocument: props,
					sellSheetId: responseJson.sellSheetId,
					purchaseOrderNumber: responseJson.purchaseOrderNumber
				});

				return responseJson;
			})
			.catch((err) => {
				setShowPreviewModal(false);
				setIsLoading(false);
				triggerToastAlert('Error', 'There was an error generating the document. Please try again.', true);
				console.error(err);
			});
		return response;
	}

	async function onPreviewClick() {
		setPdfUrl(null);
		setShowPreviewModal(true);
		const response = await loadDoc(documentGenerationType.Preview);
		const url = URL.createObjectURL(response.blob);
		setPdfFileName(response.fileName);
		setPdfUrl(url);
	}

	async function onGenerateClick() {
		const response = await loadDoc(documentGenerationType.Generate);
		const url = URL.createObjectURL(response.blob);
		navigate('/GenerateDocumentPage', {
			state: { pdfURL: url, fileName: response.fileName },
		});
	}

	function onProductClick(product) {
		setAppState({ ...appState, currentProduct: product });
		setCurrentProduct(product);
		setShowProductsModal(false);
		setProductImageSource(product.imagePath);
		navigate('/MessageSelectionPage');
	}

	function onDetailsClick() {
		setShowMessageDetailsModal(true);
	}

	function updateSelectMessageCategory(pageCategory) {
		setMessageCode(pageCategory.messageCode);
		setAppState({
			...appState,
			category: pageCategory,
			messageCode: pageCategory.messageCode,
		});
	}

	function onDetailsCategoryClick(category) {
		setPageCategory(category);
		setShowMessageDetailsModal(false);
		updateSelectMessageCategory(category);
		navigateToPage(category);
		setSelectedPlansByMessage([]);
		setSelectedMessages([]);
	}

	function handleBackButton() {
		navigate('/MessageSelectionPage');
	}

	function triggerToastAlert(title, message, autohide) {
		setToastTitle(title);
		setToastMessage(message);
		setShowToast(true);
		setToastAutohide(autohide);
	}

	function navigateToPage(category) {
		const isContainerMessage = category.isContainerMessage;
		if (category.messageType === MessageTypes.ImprovedAccess) {
			navigate('/WinPlanListPage');
		} else if (isContainerMessage) {
			navigate('/ContainerMessagePage');
		} else {
			navigate('/PlanListPage');
		}
	}

	function planListHeaderSticky() {
		const planListHeader = document.getElementById('plan-list-header');
		if (planListHeader) {
			if (scrollPosition > 150) {
				planListHeader.classList.add('plan-list-header-sticky');
				planListHeader.classList.add('border-bottom');
				document.getElementById('plan-list-header-spacer').classList.add('d-block');
				document.getElementById('plan-list-header-spacer').classList.remove('d-none');
			} else if (planListHeader.classList.contains('plan-list-header-sticky')) {
				planListHeader.classList.remove('plan-list-header-sticky');
				planListHeader.classList.remove('border-bottom');
				document.getElementById('plan-list-header-spacer').classList.remove('d-block');
				document.getElementById('plan-list-header-spacer').classList.add('d-none');
			}
		}
	}

	function closeOpenMenus(event) {
		// click outside of menus and content menus = close menus
		if (
			configMenuRef.current &&
			!configMenuRef.current.contains(event.target) &&
			!configMenuContentRef.current.contains(event.target) &&
			isConfigMenuVisible
		) {
			setIsConfigMenuVisible(false);
		}
	}

	document.addEventListener('mousedown', closeOpenMenus);

	return (
		<>
			<div className='page-content-with-sticky-actions'>
				<CreateSalesAidStepProgressBar currentStepId={SalesAidStepIds.CONFIGUREMESSAGE} focusType={appState?.focus} />

				<div className='container-fluid pt-3 pt-md-4 pb-1 color-bg-bodybg' id='plan-list-header' onScroll={planListHeaderSticky()}>
					<div className='container-xl'>
						<div className='row d-flex align-items-md-center mb-2 mb-lg-4'>
							<div className='col-12 col-md-6 order-2 order-md-1'>
								<h1 className='type-20 color-grey-d1 mt-4 mt-md-0 hide-sticky'>
									{getConfigValue(appState.config?.PlanList?.Title, appState)?.Value}
								</h1>
								<div className='row justify-content-end show-sticky-only'>
									<div className='row justify-content-start'>
										<div className='col-6 col-md-auto type-14 text-md-start'>
											<div className='color-grey-d1'>{getConfigValue(appState.config?.PlanList?.ProductLabel, appState)?.Value}</div>
											{appState.currentProduct.name}
										</div>
										<div className='col-6 col-md-auto type-14 text-md-start'>
											<div className='color-grey-d1'>{getConfigValue(appState.config?.PlanList?.MessageTypeLabel, appState)?.Value}</div>
											{pageCategory.category}
										</div>
									</div>
								</div>
							</div>
							<div className='col-12 col-md-6 order-1 order-md-2'>
								<div className='row justify-content-end'>
									<div className='col-6 col-md-auto type-14 text-md-end favorite-hcp'>
										<div className='color-grey-d1'>
											{getPrescriberTypeLabel("PlanList", prescriberType, appState)}
										</div>
										<FavoriteDesignation hcp={appState.prescriber} updateCallback={() => updateHcpAppState(appState.prescriber, setAppState)} ></FavoriteDesignation>{getName(appState)}
									</div>
									<div className='col-6 col-md-auto type-14 text-md-end'>
										<div className='color-grey-d1'>{getConfigValue(appState.config?.PlanList?.TerritoryLabel, appState)?.Value}</div>
										{appState.territory?.name}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className='my-8 d-none' id='plan-list-header-spacer'></div>

				<div className='container-xl'>
					<div className='row d-flex mb-5'>
						{/* This section will be replaced with the config data to pull in the brands */}
						<div className='col-12 col-md-4'>
							<ProductsModalButton
								setShowProductsModal={setShowProductsModal}
								productImageSource={productImageSource}
								productName={currentProduct?.productName}
							></ProductsModalButton>
						</div>

						<div className='col-12 col-md-4 mt-4 mt-md-0'>
							<CategoryDropdown pageCategory={pageCategory} onDetailsClick={onDetailsClick} isLoading={isLoading} />
						</div>

						<div className='col-12 col-md-4 mt-4 mt-md-0'>
							<PrintOptionDropdown
								isConfigMenuVisible={isConfigMenuVisible}
								setIsConfigMenuVisible={setIsConfigMenuVisible}
								configMenuContentRef={configMenuContentRef}
								configMenuRef={configMenuRef}
								configHCPisChecked={configHCPisChecked}
								setConfigHCPisChecked={setConfigHCPisChecked}
								configEHRisChecked={configEHRisChecked}
								setConfigEHRisChecked={setConfigEHRisChecked}
								isLoading={isLoading}
								selectedColumnState={selectedColumnState}
								setSelectedColumnState={setSelectedColumnState}
								printConfigColumnHeaders={hasPlanTable ? plans?.headers : null}
								isDisabled={false}
								printOptionsEhrBackPageEnabled={printOptionsEhrBackPageEnabled}
								printOptionsCoverPageEnabled={printOptionsCoverPageEnabled}
								printOptionsHcpNameEnabled={printOptionsHcpNameEnabled}
								printOptionsLocationNameEnabled={printOptionsLocationNameEnabled}
								configCoverisChecked={printOptionsCoverPageIsChecked}
								setConfigCoverisChecked={setPrintOptionsCoverPageIsChecked}
								setConfigLocationNameIsChecked={setConfigLocationNameIsChecked}
								configLocationNameIsChecked={configLocationNameIsChecked}
								savingsCardIsChecked={savingsCardIsChecked}
								setSavingsCardIschecked={setSavingsCardIsChecked}
								savingsCardIsEnabled={savingsCardIsEnabled}
								canSelectSavingsCard={selectableOptions.canSelectSavingsCard}
								canSelectSalesRepNameInfo={printOptionsSelectSalesRepInfoEnabled}
								canSelectFRMInfo={printOptionsSelectFRMInfoEnabled}
								printSalesRepoInfoIsChecked={printOptionsSalesRepInfoIsChecked}
								printFRMInfoIsChecked={printOptionsFRMInfoInfoIsChecked}
								setPrintSalesRepoInfoIsChecked={setPrintOptionsSalesRepInfoIsChecked}
								setPrintFRMInfoIsChecked={setPrintOptionsFRMInfoInfoIsChecked}
								hcpNameRequired={hcpNameRequired}
								ehrBackPageRequired={ehrBackPageRequired}
								coverPageRequired={hcpCoverPageRequired}
								savingsCardRequired={savingsCardRequired}
								locationNameRequired={locationNameRequired}
								salesRepInfoRequired={salesRepInfoRequired}
								frmInfoRequired={frmInfoRequired}
								onOptionsClick={(data) => { return executePrintOptionRestrictions(data, parentState, setSelectableOptions, showTooManyOptionsSelectedMessage) }}
							/>
						</div>
					</div>

					<div className='row'>
						<div className='col-12'>
							<div className='alert alert-primary' role='alert'>
								<div className='alert-icon'></div>
								<div className='alert-content'>
									<p className='m-0'>{getConfigValue(appState?.config?.PlanList?.Container?.SelectionMessage, appState)?.Value}</p>
								</div>
							</div>
						</div>
					</div>

				{hasPlanTable
					? prescriberMessages?.map((message, index) => (
						<ContainerMessageTable
							key={`message_table_${message.prescriberMessage.id}`}
							updateSelectedPlans={updateSelectedPlans}
							message={message}
							parentState={parentState}
							setSelectableOptions={setSelectableOptions}
							triggerToastAlert={triggerToastAlert}
							executePrintOptionRestrictions={executePrintOptionRestrictions}
							showTooManyOptionsSelectedMessage={showTooManyOptionsSelectedMessage}
						/>
					))
					: (
						<div className='row mt-3'>
							<div className='col-12'>
								{prescriberMessages?.map((message, index) => (
									<div key={message.prescriberMessage.id} className='card pd-card px-0 py-0 mb-3'>
										<div className='d-flex align-items-center py-3 px-4'>
											<input
												className='form-check-input me-3 flex-shrink-0'
												type='checkbox'
												id={`message_check_${message.prescriberMessage.id}`}
												checked={isMessageSelected(message)}
												onChange={(e) => handleMessageCheckboxChange(message, e.target.checked)}
											/>
											<label
												className='form-check-label type-16 text-primary'
												htmlFor={`message_check_${message.prescriberMessage.id}`}
											>
												<span dangerouslySetInnerHTML={{ __html: message.prescriberMessage.mainMessage }} />
											</label>
										</div>
									</div>
									))}
							</div>
						</div>
					)
				}
				</div>
			</div>

			<div className='button-bottom-container-sticky-low-z'>
				<div className='container-xl'>
					<div className='row py-3'>
						<div className='col-5'>
							<div className='d-flex flex-column flex-lg-row'>
								<button
									onClick={onPreviewClick}
									disabled={!isValid || isLoading}
									className={`btn btn-outline-primary btn-lg flex-fill w-100 ${isValid || isLoading ? '' : 'disabled'}`}
								>
									{getConfigValue(appState.config?.PlanList?.Preview.Label, appState)?.Value || null}
								</button>
								<div className='flex-fill w-100 ms-lg-3 pe-3'></div>
							</div>
						</div>
						<div className='col-7'>
							<div className='d-flex flex-md-row-reverse'>
								<button
									className={`btn btn-success btn-lg mb-lg-0 ms-lg-3 flex-fill w-100 ml-1 ${isValid || isLoading ? '' : 'disabled'} ${isLoading ? 'btn-working' : ''
										}`}
									type='button'
									disabled={!isValid || isLoading}
									onClick={onGenerateClick}
								>
									{' '}
									{getConfigValue(appState.config?.PlanList?.Generate?.Label, appState)?.Value || null}
								</button>
								<button type='button' onClick={handleBackButton} className='btn btn-secondary btn-lg flex-fill w-100'>
									{getConfigValue(appState.config?.Common?.Back, appState)?.Value || null}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<ToastAlert show={showToast} setShow={setShowToast} title={toastTitle} message={toastMessage} autohide={toastAutohide} />

			<MessageDetailsModal
				show={showMessageDetailsModal}
				setShow={setShowMessageDetailsModal}
				categories={categories}
				onDetailsCategoryClick={onDetailsCategoryClick}
			/>
			<PreviewModal
				show={showPreviewModal}
				setShow={setShowPreviewModal}
				onGenerateClick={onGenerateClick}
				isValid={isValid}
				isLoading={isLoading}
				pdfUrl={pdfUrl}
				showGenerateBack={true}
				showPrintDownload={false}
				pdfFileName={pdfFileName}
				supportsProfessionalPrinting={true}
				skipPreviewTrack={true}
				returnURL={'/containerMessagePage'}
			/>

			<ProductsModal show={showProductsModal} setShow={setShowProductsModal} onProductClick={onProductClick} onlySellSheetProducts={true} />
		</>
	);
}

export default ContainerMessagePage;
